import {
  Component,
  HostListener,
  Input,
  computed,
  forwardRef,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { startOfDay } from 'date-fns';

import {
  SUI_FORM_FIELD_CONTROL,
  SuiFormFieldControl,
} from '../form-field/form-field-control';
import { SuiInputComponent } from '../input';
import { SuiFormControlBase } from '../shared/cva-helper';

type Unit = 'hour' | 'minute' | 'second';
type Meridian = 'AM' | 'PM';

/**
 * Hand-built time-of-day stepper — ng-primitives ships **no** time primitive
 * (only the `date-time` adapter abstraction), so unlike `sui-date-picker` this
 * owns its own keyboard/step contract. Rebuilds `ngb-timepicker`'s stacked
 * spinner (chevron▲ / numeric `sui-input` / chevron▼ per unit) on the sui
 * box-control, restyled to sui tokens.
 *
 * Public value type is native **`Date | null`** — the time-of-day rides on a
 * Date (consistent with `sui-date-picker`; the date part is preserved from the
 * written value so this composes cleanly inside `sui-date-time-picker`). `null`
 * externally; the value stays `null` until every *required* segment (hour +
 * minute, and second when `[seconds]`) is present.
 *
 * Internals keep hours as **24h** (`_h` 0–23) regardless of `[meridian]`; the
 * field display + AM/PM derive from it, so stepping/wrapping is one source of
 * truth. Each segment is a `sui-input`; stepping is chevron-click + ↑/↓ keys +
 * scroll wheel, all wrap-around within their own field (no carry) like ngb.
 */
@Component({
  selector: 'sui-time-picker',
  standalone: true,
  imports: [FormsModule, FaIconComponent, SuiInputComponent],
  templateUrl: './sui-time-picker.component.html',
  styleUrl: './sui-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiTimePickerComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiTimePickerComponent),
    },
  ],
})
export class SuiTimePickerComponent
  extends SuiFormControlBase<Date>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-time-picker-${++SuiTimePickerComponent._idCounter}`;
  private _focused = false;

  /** Date part carried through unchanged; the time is written onto a copy. */
  private _baseDate: Date = startOfDay(new Date());

  readonly faUp = faAngleUp;
  readonly faDown = faAngleDown;

  readonly controlType = 'time-picker';

  @Input() id = '';
  /** Show the seconds segment. */
  @Input() seconds = false;
  /** 12-hour clock with an AM/PM toggle (default 24h). */
  @Input() meridian = false;
  @Input() hourStep = 1;
  @Input() minuteStep = 1;
  @Input() secondStep = 1;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }
  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return !this.value();
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  override writeValue(value: Date | null): void {
    if (value) {
      this._baseDate = startOfDay(value);
      this._setFromDate(value);
    } else {
      this._baseDate = startOfDay(new Date());
      this._clearSegments();
    }
    this.value.set(value);
  }

  // ── Segment input (typing) ───────────────────────────────────────────────
  onInput(unit: Unit, raw: string): void {
    this._text(unit).set(raw);
    const trimmed = raw.trim();
    if (trimmed === '') {
      this._num(unit).set(null);
      this._recompose();
      return;
    }
    const n = parseInt(trimmed, 10);
    if (isNaN(n)) {
      this._recompose();
      return;
    }
    if (unit === 'hour') {
      this._setHourFromDisplay(n);
    } else {
      this._num(unit).set(this._clamp(n, 0, 59));
    }
    this._recompose();
  }

  /**
   * The segment being typed in. An external write (the host's ngModel echo of
   * our own change) must not pad its text under the caret - "1" would become
   * "01" mid-word and the next digit would land after it (WAP-1664).
   */
  private _focusedUnit: Unit | null = null;

  onFocus(unit: Unit): void {
    this._focusedUnit = unit;
  }

  /** Blur: canonicalise the segment text from its committed value (pad / clear). */
  onBlur(unit: Unit): void {
    if (this._focusedUnit === unit) {
      this._focusedUnit = null;
    }
    this._syncText(unit);
    this.markTouched();
  }

  // ── Stepping (chevrons / arrow keys / wheel) ───────────────────────────────
  step(unit: Unit, dir: 1 | -1): void {
    if (this.isDisabled()) {
      return;
    }
    // First interaction on an empty picker seeds 00:00(:00) so a single spin
    // yields a complete value (time pickers default to midnight, not partial).
    this._seedIfEmpty();
    const by = dir * this._stepFor(unit);
    if (unit === 'hour') {
      this._h.set(this._wrap((this._h() ?? 0) + by, 24));
      this._syncMeridianFromHour();
    } else {
      this._num(unit).set(this._wrap((this._num(unit)() ?? 0) + by, 60));
    }
    this._syncText(unit);
    this._recompose();
  }

  onWheel(unit: Unit, event: WheelEvent): void {
    if (!this._focused || this.isDisabled()) {
      return;
    }
    event.preventDefault();
    this.step(unit, event.deltaY < 0 ? 1 : -1);
  }

  toggleMeridian(): void {
    if (this.isDisabled()) {
      return;
    }
    const next: Meridian = this.meridianValue() === 'AM' ? 'PM' : 'AM';
    this._meridian.set(next);
    if (this._h() !== null) {
      this._h.set(this._wrap(this._h()! + 12, 24));
    }
    this._recompose();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }
  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  // ── Committed numeric state (hours always 24h) + per-segment edit buffers ──
  private readonly _h = signal<number | null>(null);
  private readonly _m = signal<number | null>(null);
  private readonly _s = signal<number | null>(null);
  private readonly _meridian = signal<Meridian>('AM');

  readonly hourText = signal<string>('');
  readonly minuteText = signal<string>('');
  readonly secondText = signal<string>('');

  /** AM/PM for the toggle button — derived from the committed hour when set. */
  readonly meridianValue = computed<Meridian>(() => this._meridian());

  private _num(unit: Unit) {
    return unit === 'hour' ? this._h : unit === 'minute' ? this._m : this._s;
  }
  private _text(unit: Unit) {
    return unit === 'hour'
      ? this.hourText
      : unit === 'minute'
        ? this.minuteText
        : this.secondText;
  }
  private _stepFor(unit: Unit): number {
    const raw =
      unit === 'hour'
        ? this.hourStep
        : unit === 'minute'
          ? this.minuteStep
          : this.secondStep;
    return raw > 0 ? raw : 1;
  }

  private _setHourFromDisplay(n: number): void {
    if (this.meridian) {
      const d = this._clamp(n, 1, 12) % 12;
      this._h.set(this._meridian() === 'PM' ? d + 12 : d);
    } else {
      const h = this._clamp(n, 0, 23);
      this._h.set(h);
      this._syncMeridianFromHour();
    }
  }

  private _syncMeridianFromHour(): void {
    const h = this._h();
    if (h !== null) {
      this._meridian.set(h < 12 ? 'AM' : 'PM');
    }
  }

  private _seedIfEmpty(): void {
    if (this.value() === null) {
      this._h.set(this._h() ?? 0);
      this._m.set(this._m() ?? 0);
      if (this.seconds) {
        this._s.set(this._s() ?? 0);
      }
    }
  }

  private _setFromDate(date: Date): void {
    this._h.set(date.getHours());
    this._m.set(date.getMinutes());
    this._s.set(date.getSeconds());
    this._syncMeridianFromHour();
    (['hour', 'minute', 'second'] as Unit[])
      .filter((u) => u !== this._focusedUnit)
      .forEach((u) => this._syncText(u));
  }

  private _clearSegments(): void {
    this._h.set(null);
    this._m.set(null);
    this._s.set(null);
    this._meridian.set('AM');
    this.hourText.set('');
    this.minuteText.set('');
    this.secondText.set('');
  }

  /** Mirror a committed segment back into its display text (padded / cleared). */
  private _syncText(unit: Unit): void {
    const text = this._text(unit);
    if (unit === 'hour') {
      const h = this._h();
      text.set(h === null ? '' : this._pad(this._displayHour(h)));
    } else {
      const n = this._num(unit)();
      text.set(n === null ? '' : this._pad(n));
    }
  }

  /** Build + emit the Date once every required segment is present; else null. */
  private _recompose(): void {
    const h = this._h();
    const m = this._m();
    const s = this.seconds ? this._s() : 0;
    if (h === null || m === null || (this.seconds && this._s() === null)) {
      this.setValue(null);
      return;
    }
    const out = new Date(this._baseDate);
    out.setHours(h, m, s ?? 0, 0);
    this.setValue(out);
  }

  private _displayHour(h: number): number {
    return this.meridian ? h % 12 || 12 : h;
  }
  private _clamp(n: number, lo: number, hi: number): number {
    return Math.min(hi, Math.max(lo, n));
  }
  private _wrap(n: number, mod: number): number {
    return ((n % mod) + mod) % mod;
  }
  private _pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
